import React, { useEffect, useState } from "react";
import { Star, Inbox, FileText, Download, Mail, MessageCircle, Sparkles, TrendingUp, ArrowUp, ArrowDown, Minus, Send, Loader2, CalendarDays, Megaphone, Activity, Users } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import { adminApi, publicApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import LeadListModal from "./LeadListModal";
import { rowsToCsv, downloadCsv } from "../../lib/csv";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Input } from "../ui/input";

// Returns {from,to} YYYY-MM-DD for a named preset (local time to avoid UTC date drift).
const presetRange = (key) => {
  const now = new Date();
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (key === "7") { const f = new Date(now); f.setDate(f.getDate() - 6); return { from: iso(f), to: iso(now) }; }
  if (key === "30") { const f = new Date(now); f.setDate(f.getDate() - 29); return { from: iso(f), to: iso(now) }; }
  if (key === "thisMonth") return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
  if (key === "lastMonth") return { from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
  if (key === "year") return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) };
  return { from: "", to: "" };
};

const COL = (key, label, type) => ({ key, label, ...(type ? { type } : {}) });
const CONSENT = COL("marketing_consent", "Marketing consent", "consent");
// Per-box lead-list configuration: how to fetch the people captured & what columns to show/export.
const LEAD_LISTS = {
  "Total enquiries": {
    title: "Enquiries", filename: "enquiries",
    fetcher: () => adminApi.enquiries.list(),
    columns: [COL("name", "Name"), COL("email", "Email"), COL("phone", "Phone"), COL("business_name", "Business"), COL("service", "Service"), COL("message", "Message"), COL("source", "Source"), CONSENT, COL("created_at", "Date")],
  },
  "Quote requests": {
    title: "Quote requests", filename: "quote-requests",
    fetcher: () => adminApi.quotes.list(),
    columns: [COL("name", "Name"), COL("email", "Email"), COL("phone", "Phone"), COL("business_name", "Business"), COL("reason", "Reason"), COL("business_stage", "Stage"), CONSENT, COL("created_at", "Date")],
  },
  "Guide downloads": {
    title: "Guide downloads", filename: "guide-downloads",
    fetcher: () => adminApi.guideLeads.list(),
    columns: [COL("name", "Name"), COL("email", "Email"), COL("guide_title", "Guide"), CONSENT, COL("created_at", "Date")],
  },
  "Cassie leads": {
    title: "Cassie leads", filename: "cassie-leads",
    fetcher: () => adminApi.enquiries.list().then((rows) => (rows || []).filter((e) => e.source === "Cassie AI chatbot")),
    columns: [COL("name", "Name"), COL("email", "Email"), COL("phone", "Phone"), COL("message", "Message"), COL("created_at", "Date")],
  },
  "Subscribers": {
    title: "Subscribers", filename: "subscribers",
    fetcher: () => adminApi.subscribers.list(),
    columns: [COL("name", "Name"), COL("email", "Email"), COL("source", "Source"), COL("created_at", "Date")],
  },
  "Health Checks": {
    title: "Health Check completions", filename: "health-checks",
    fetcher: () => adminApi.healthCheck.leads(),
    columns: [COL("name", "Name"), COL("email", "Email"), COL("business_name", "Business"), COL("phone", "Phone"), COL("overall_score", "Score"), COL("overall_label", "Result"), CONSENT, COL("created_at", "Date")],
  },
};

const SOURCE_COLORS = ["#2EA3FD", "#F5821F", "#6366F1", "#10B981", "#F59E0B"];
const QUALITY_COLORS = { High: "#10B981", Medium: "#F59E0B", Low: "#94A3B8" };

const DeltaPill = ({ delta }) => {
  if (!delta) return null;
  const { dir, pct, is_new, last, prev } = delta;
  if (last === 0 && prev === 0) return <span className="text-[11px] font-semibold text-slate-300">No activity yet</span>;
  const cfg = is_new
    ? { icon: ArrowUp, cls: "text-emerald-600 bg-emerald-50", text: "New" }
    : dir === "up"
    ? { icon: ArrowUp, cls: "text-emerald-600 bg-emerald-50", text: `+${pct}%` }
    : dir === "down"
    ? { icon: ArrowDown, cls: "text-rose-600 bg-rose-50", text: `${pct}%` }
    : { icon: Minus, cls: "text-slate-500 bg-slate-100", text: "0%" };
  const Icon = cfg.icon;
  return (
    <span data-testid="delta-pill" title={`${last} in last 30 days vs ${prev} in the previous 30 days`}
      className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${cfg.cls}`}>
      <Icon size={12} /> {cfg.text}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color, delta, onClick }) => {
  const clickable = !!onClick;
  return (
    <div
      data-testid={`analytics-stat-${label}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={`bg-white rounded-2xl border border-slate-100 p-5 shadow-sm ${clickable ? "cursor-pointer hover:shadow-md hover:border-orange/40 hover:-translate-y-0.5 transition-all" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}><Icon size={18} /></div>
        <DeltaPill delta={delta} />
      </div>
      <p className="font-display font-extrabold text-3xl text-navy leading-none">{value}</p>
      <p className="text-xs font-semibold text-slate-500 mt-1.5">{label}</p>
      {clickable ? (
        <p className="text-[11px] text-orange font-semibold mt-0.5" data-testid={`analytics-stat-viewlist-${label}`}>View &amp; export list →</p>
      ) : (
        <p className="text-[11px] text-slate-400 mt-0.5">vs previous 30 days</p>
      )}
    </div>
  );
};

const Panel = ({ icon: Icon, iconCls, title, subtitle, children, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
    <div className="flex items-center gap-3 mb-5">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${iconCls}`}><Icon size={20} /></div>
      <div>
        <h2 className="font-display font-bold text-navy">{title}</h2>
        <p className="text-xs text-slate-400">{subtitle}</p>
      </div>
    </div>
    {children}
  </div>
);

const Empty = ({ children }) => <p className="text-sm text-slate-400 py-8 text-center">{children}</p>;

const SectionHeading = ({ title, subtitle }) => (
  <div className="mt-10 mb-1 pb-2 border-b border-slate-200/80">
    <h2 className="font-display font-extrabold text-lg text-navy">{title}</h2>
    {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
  </div>
);

export default function AnalyticsManager() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const [rating, setRating] = useState(null);
  const [activeList, setActiveList] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [mcSyncing, setMcSyncing] = useState(false);
  const [mcStatus, setMcStatus] = useState(null);

  useEffect(() => {
    adminApi.mailchimp.status().then(setMcStatus).catch(() => setMcStatus(null));
  }, []);

  const syncMailchimp = async () => {
    setMcSyncing(true);
    try {
      const res = await adminApi.mailchimp.syncAll();
      if (res?.ok) {
        toast({ title: "Synced to Mailchimp", description: `${res.synced} of ${res.total} contacts sent${res.failed ? ` · ${res.failed} failed` : ""}.` });
        adminApi.mailchimp.status().then(setMcStatus).catch(() => {});
      } else {
        toast({ title: "Mailchimp not configured", description: res?.message || "Add your Mailchimp keys on the server.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Couldn't sync to Mailchimp", variant: "destructive" });
    } finally {
      setMcSyncing(false);
    }
  };

  useEffect(() => {
    adminApi.analytics().then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    publicApi.googleRating().then(setRating).catch(() => {});
  }, []);

  const emailSnapshot = async () => {    setEmailing(true);    try {
      const res = await adminApi.emailAnalytics();
      if (res?.result?.sent) {
        toast({ title: "Snapshot emailed", description: "Check your admin inbox for the analytics summary." });
      } else {
        toast({ title: "Couldn't send email", description: res?.result?.error || "Email is not configured on the server.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Couldn't send email", variant: "destructive" });
    } finally {
      setEmailing(false);
    }
  };

  // Export every captured lead across all channels into one CSV, tagged with how it was obtained.
  const exportAllLeads = async (range = {}) => {
    const { from, to } = range;
    setExporting(true);
    try {
      const [subs, guides, hcs, enqs, qts] = await Promise.all([
        adminApi.subscribers.list().catch(() => []),
        adminApi.guideLeads.list().catch(() => []),
        adminApi.healthCheck.leads().catch(() => []),
        adminApi.enquiries.list().catch(() => []),
        adminApi.quotes.list().catch(() => []),
      ]);
      const rows = [];
      const add = (arr, how, consentDefault) => (arr || []).forEach((r) => rows.push({
        name: r.name || "", email: r.email || "", source: how(r),
        business: r.business_name || "", phone: r.phone || "", created_at: r.created_at || "",
        marketing_consent: consentDefault !== undefined ? consentDefault : !!r.marketing_consent,
      }));
      add(subs, (r) => r.source || "Newsletter / Podcast", true);
      add(guides, (r) => `Guide download${r.guide_title ? `: ${r.guide_title}` : ""}`);
      add(hcs, () => "Business Growth Health Check");
      add(enqs, (r) => (r.source === "Cassie AI chatbot" ? "Cassie AI chatbot" : (r.source || "Contact enquiry")));
      add(qts, () => "Quote request");
      const filtered = rows.filter((r) => {
        const day = String(r.created_at).slice(0, 10);
        if (from && (!day || day < from)) return false;
        if (to && (!day || day > to)) return false;
        return true;
      });
      if (filtered.length === 0) {
        toast({ title: from || to ? "No leads in that date range" : "No leads to export yet" });
        return;
      }
      filtered.sort((a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0));
      const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");
      const columns = [
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "source", label: "How obtained" },
        { key: "business", label: "Business" },
        { key: "phone", label: "Phone" },
        { key: "marketing_consent", label: "Marketing consent" },
        { key: "created_at", label: "Date" },
      ];
      const csv = rowsToCsv(columns, filtered, (r, c) => (c.key === "created_at" ? fmtDate(r[c.key]) : c.key === "marketing_consent" ? (r[c.key] ? "Yes" : "No") : r[c.key]));
      const suffix = from || to ? `${from || "start"}_to_${to || "today"}` : new Date().toISOString().slice(0, 10);
      downloadCsv(`all-leads-${suffix}.csv`, csv);
      toast({ title: "All leads exported", description: `${filtered.length} contacts downloaded as CSV.` });
      setRangeOpen(false);
    } catch {
      toast({ title: "Couldn't export leads", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <p className="text-sm text-slate-400" data-testid="analytics-loading">Loading analytics…</p>;
  if (!data) return <p className="text-sm text-red-500" data-testid="analytics-error">Could not load analytics.</p>;

  const t = data.totals;
  const d = data.deltas || {};
  const wd = data.week_delta || {};
  const weekly = data.weekly || [];
  const weekHasData = weekly.some((w) => w.leads);
  const trendHasData = data.monthly.some((m) => m.enquiries || m.quotes || m.guides);
  const topics = data.chat_topics || [];
  const maxTopic = topics.length ? topics[0].count : 1;
  const guideBreakdown = data.guide_breakdown || [];
  const maxGuide = guideBreakdown.reduce((m, g) => Math.max(m, g.count), 0) || 1;
  const promo = data.promo_stats;
  const hc = data.health_check_stats || { total: 0, avg_overall: 0, areas: [], bands: [] };

  const cards = [
    { icon: Inbox, label: "Total enquiries", value: t.enquiries, color: "text-sky-600 bg-sky-50", delta: d.enquiries },
    { icon: FileText, label: "Quote requests", value: t.quotes, color: "text-indigo-600 bg-indigo-50", delta: d.quotes },
    { icon: Download, label: "Guide downloads", value: t.guide_downloads, color: "text-emerald-600 bg-emerald-50", delta: d.guide_downloads },
    { icon: MessageCircle, label: "Cassie messages", value: t.chat_messages, color: "text-orange bg-orange/10", delta: d.chat_messages },
    { icon: Sparkles, label: "Cassie leads", value: t.cassie_leads, color: "text-fuchsia-600 bg-fuchsia-50", delta: d.cassie_leads },
    { icon: Mail, label: "Subscribers", value: t.subscribers, color: "text-teal-600 bg-teal-50", delta: d.subscribers },
    { icon: Activity, label: "Health Checks", value: t.health_checks, color: "text-rose-600 bg-rose-50", delta: d.health_checks },
  ].map((c) => (LEAD_LISTS[c.label] ? { ...c, onClick: () => setActiveList(LEAD_LISTS[c.label]) } : c));

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <div className="flex items-center gap-3">
          <span className="h-7 w-1.5 rounded-full bg-orange" />
          <h1 className="font-display font-extrabold text-2xl md:text-3xl text-navy">Analytics</h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Popover open={rangeOpen} onOpenChange={setRangeOpen}>
            <PopoverTrigger asChild>
              <Button
                data-testid="export-all-leads-btn"
                disabled={exporting}
                variant="outline"
                className="rounded-full border-slate-200 text-navy hover:bg-slate-50"
              >
                {exporting ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Exporting…</> : <><Users size={15} className="mr-1.5" /> Export all leads</>}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72" data-testid="export-range-popover">
              <p className="font-display font-bold text-navy text-sm mb-3">Export leads by date</p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {[
                  { k: "7", l: "Last 7 days" },
                  { k: "30", l: "Last 30 days" },
                  { k: "thisMonth", l: "This month" },
                  { k: "lastMonth", l: "Last month" },
                  { k: "year", l: "This year" },
                  { k: "all", l: "All time" },
                ].map((p) => (
                  <button
                    key={p.k}
                    data-testid={`export-preset-${p.k}`}
                    onClick={() => { const r = presetRange(p.k); setRangeFrom(r.from); setRangeTo(r.to); }}
                    className="text-xs font-semibold px-2.5 py-2 rounded-lg border border-slate-200 text-navy hover:bg-orange/10 hover:border-orange/40 transition-colors"
                  >
                    {p.l}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div>
                  <label className="text-[11px] font-semibold text-slate-500">From</label>
                  <Input type="date" data-testid="export-range-from" value={rangeFrom} max={rangeTo || undefined} onChange={(e) => setRangeFrom(e.target.value)} className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-500">To</label>
                  <Input type="date" data-testid="export-range-to" value={rangeTo} min={rangeFrom || undefined} onChange={(e) => setRangeTo(e.target.value)} className="mt-1 h-9 text-sm" />
                </div>
              </div>
              <Button
                data-testid="export-range-download"
                onClick={() => exportAllLeads({ from: rangeFrom, to: rangeTo })}
                disabled={exporting}
                className="w-full bg-orange hover:bg-orange-dark text-white rounded-full h-9"
              >
                {exporting ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Exporting…</> : <><Download size={15} className="mr-1.5" /> Download CSV{rangeFrom || rangeTo ? " (range)" : " (all)"}</>}
              </Button>
            </PopoverContent>
          </Popover>
          <Button
            data-testid="mailchimp-sync-btn"
            onClick={syncMailchimp}
            disabled={mcSyncing}
            variant="outline"
            title={mcStatus?.connected ? "Push all captured contacts to your Mailchimp audience" : "Mailchimp connection unavailable"}
            className="rounded-full border-slate-200 text-navy hover:bg-slate-50"
          >
            {mcSyncing ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Syncing…</> : <><Mail size={15} className="mr-1.5" /> Sync to Mailchimp</>}
          </Button>
          <Button
            data-testid="email-snapshot-btn"
            onClick={emailSnapshot}
            disabled={emailing}
            className="bg-orange hover:bg-orange-dark text-white rounded-full"
          >
            {emailing ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Sending…</> : <><Send size={15} className="mr-1.5" /> Email me this snapshot</>}
          </Button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-8 pl-4">Turn the leads and conversations you're already capturing into insight.</p>

      {mcStatus?.configured && (
        <div data-testid="mailchimp-status-card" className="mb-8 flex items-center gap-3 rounded-2xl border border-slate-100 bg-white shadow-sm px-5 py-4">
          <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${mcStatus.connected ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
            <Mail size={18} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-navy flex items-center gap-2">
              {mcStatus.connected ? "Connected to Mailchimp" : "Mailchimp — connection issue"}
              <span className={`w-2 h-2 rounded-full ${mcStatus.connected ? "bg-emerald-500" : "bg-rose-500"}`} />
            </p>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="mailchimp-status-count">
              {mcStatus.connected
                ? `${mcStatus.member_count} contact${mcStatus.member_count === 1 ? "" : "s"} in ${mcStatus.audience_name || "your audience"}`
                : "Couldn't reach Mailchimp — check the API key on the server."}
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <Panel testid="analytics-week" icon={CalendarDays} iconCls="bg-emerald-50 text-emerald-600" title="Leads this week" subtitle="New enquiries, quote requests and guide downloads — last 8 weeks.">
            <div className="flex items-center gap-4 mb-4">
              <span className="font-display font-extrabold text-4xl text-navy leading-none" data-testid="week-leads-value">{wd.last ?? 0}</span>
              <div className="flex flex-col">
                <DeltaPill delta={wd} />
                <span className="text-[11px] text-slate-400 mt-1">vs {wd.prev ?? 0} last week</span>
              </div>
            </div>
            {weekHasData ? (
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={weekly} margin={{ top: 5, right: 6, left: -28, bottom: 0 }}>
                  <defs>
                    <linearGradient id="weekFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={34} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eef1f5", fontSize: 13 }} />
                  <Area type="monotone" dataKey="leads" name="Leads" stroke="#10B981" strokeWidth={2.5} fill="url(#weekFill)" dot={{ r: 2.5 }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <Empty>No leads captured in the last 8 weeks yet. This sparkline fills in as leads come through.</Empty>}
          </Panel>
        </div>
        <Panel testid="analytics-google" icon={Star} iconCls="bg-amber-50 text-amber-500" title="Google rating" subtitle="Live from your Google Business Profile.">
          {rating?.rating ? (
            <div className="flex flex-col items-start">
              <div className="flex items-baseline gap-1">
                <span className="font-display font-extrabold text-5xl text-navy leading-none" data-testid="analytics-google-value">{rating.rating.toFixed(1)}</span>
                <span className="text-lg text-slate-400 font-semibold">/5</span>
              </div>
              <div className="flex items-center gap-0.5 mt-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={18} className={i <= Math.round(rating.rating) ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200"} />
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-3">{rating.total} Google reviews</p>
              {(rating.url || rating.write_review_url) && (
                <a href={rating.url || rating.write_review_url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-orange hover:underline mt-2">View on Google →</a>
              )}
            </div>
          ) : <Empty>Google rating unavailable right now.</Empty>}
        </Panel>
      </div>

      <SectionHeading title="Overview" subtitle="Your key numbers at a glance." />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6 mt-4">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>

      <SectionHeading title="Leads & enquiries" subtitle="Where your leads come from and how they trend." />
      <Panel testid="analytics-trend" icon={TrendingUp} iconCls="bg-orange/10 text-orange" title="Leads over time" subtitle="Enquiries, quote requests and guide downloads — last 6 months.">
        {trendHasData ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.monthly} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eef1f5", fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="enquiries" name="Enquiries" stroke="#2EA3FD" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="quotes" name="Quotes" stroke="#F5821F" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="guides" name="Guide downloads" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty>No leads recorded in the last 6 months yet. This chart fills in as enquiries, quotes and downloads come through.</Empty>}
      </Panel>

      <div className="mt-5">
        <Panel testid="analytics-sources" icon={FileText} iconCls="bg-sky-50 text-sky-600" title="Where leads come from" subtitle="Split of all leads by channel.">
          {data.lead_sources.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.lead_sources} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {data.lead_sources.map((s, i) => <Cell key={s.name} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eef1f5", fontSize: 13 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty>No leads captured yet.</Empty>}
        </Panel>
      </div>

      <SectionHeading title="Content performance" subtitle="How your guides and homepage promo are performing." />
      <div className="grid lg:grid-cols-2 gap-5 mt-4">
        <Panel testid="analytics-guide-breakdown" icon={Download} iconCls="bg-emerald-50 text-emerald-600" title="Downloads by guide" subtitle="Which guides generate the most downloads.">
          {guideBreakdown.length ? (
            <div className="space-y-3 mt-1">
              {guideBreakdown.map((g, i) => {
                const pct = Math.round((g.count / maxGuide) * 100);
                return (
                  <div key={g.title + i} data-testid={`guide-breakdown-${i}`} className="flex items-center gap-4">
                    <span className="w-5 text-xs font-bold text-slate-300 shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-navy truncate">{g.title}</span>
                        <span className="text-xs font-bold text-slate-500 shrink-0 ml-3">{g.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <Empty>No guides yet. Add guides to start tracking downloads per guide.</Empty>}
        </Panel>

        <Panel testid="analytics-promo" icon={Megaphone} iconCls="bg-violet-50 text-violet-600" title="Homepage promo popup" subtitle="Views, clicks and click-through rate of your promo.">
          {promo && (promo.impressions > 0 || promo.clicks > 0) ? (
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div className="rounded-xl bg-slate-50 p-4 text-center">
                <p className="font-display font-extrabold text-3xl text-navy" data-testid="analytics-promo-views">{promo.impressions}</p>
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mt-1">Views</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4 text-center">
                <p className="font-display font-extrabold text-3xl text-navy" data-testid="analytics-promo-clicks">{promo.clicks}</p>
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mt-1">Clicks</p>
              </div>
              <div className="rounded-xl bg-orange/5 p-4 text-center">
                <p className="font-display font-extrabold text-3xl text-orange" data-testid="analytics-promo-ctr">{promo.ctr}%</p>
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mt-1">Click rate</p>
              </div>
            </div>
          ) : <Empty>No promo activity yet. Turn on a homepage promo and its views &amp; clicks will show here.</Empty>}
        </Panel>
      </div>

      <SectionHeading title="Business Growth Health Check" subtitle="Completions and where prospects struggle most — use this to shape your messaging and follow-ups." />
      <div className="grid lg:grid-cols-2 gap-5 mt-4">
        <Panel testid="analytics-health-averages" icon={Activity} iconCls="bg-rose-50 text-rose-600" title="Average score by area" subtitle="Lower bars = the pain points prospects report most.">
          {hc.total ? (
            <div className="space-y-3 mt-1">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 p-4 mb-2">
                <span className="text-sm font-semibold text-navy">Overall average</span>
                <span className="font-display font-extrabold text-2xl text-navy" data-testid="analytics-health-avg-overall">{hc.avg_overall}<span className="text-sm text-slate-400">/40</span></span>
              </div>
              {hc.areas.map((a, i) => {
                const pct = Math.round((a.avg / 10) * 100);
                const barColor = pct >= 80 ? "bg-green-500" : pct >= 60 ? "bg-amber-500" : "bg-rose-500";
                return (
                  <div key={a.title + i} data-testid={`health-area-avg-${i}`} className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-navy truncate">{a.title}</span>
                        <span className="text-xs font-bold text-slate-500 shrink-0 ml-3">{a.avg}/10</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <Empty>No Health Check completions yet. Once visitors complete it, average scores by area will appear here.</Empty>}
        </Panel>

        <Panel testid="analytics-health-bands" icon={Activity} iconCls="bg-rose-50 text-rose-600" title="Results breakdown" subtitle="How completed businesses scored overall.">
          {hc.total ? (
            <div className="space-y-2 mt-1">
              <p className="text-sm text-slate-500 mb-3"><strong className="text-navy" data-testid="analytics-health-total">{hc.total}</strong> completion{hc.total === 1 ? "" : "s"} so far.</p>
              {hc.bands.map((b, i) => (
                <div key={b.label + i} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3" data-testid={`health-band-${i}`}>
                  <span className="text-sm font-semibold text-navy">{b.label}</span>
                  <span className="text-sm font-bold text-slate-500">{b.count}</span>
                </div>
              ))}
            </div>
          ) : <Empty>No completions to summarise yet.</Empty>}
        </Panel>
      </div>

      <div className="mt-4">
      <Panel testid="analytics-topics" icon={MessageCircle} iconCls="bg-orange/10 text-orange" title="Most-asked Cassie topics" subtitle="What visitors ask your chatbot about most." >
        <div className="mt-1">
        {topics.length ? (
            <div className="space-y-3">
              {topics.map((tp, i) => {
                const pct = Math.round((tp.count / maxTopic) * 100);
                return (
                  <div key={tp.topic} data-testid={`topic-${i}`} className="flex items-center gap-4">
                    <span className="w-5 text-xs font-bold text-slate-300 shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-navy truncate">{tp.topic}</span>
                        <span className="text-xs font-bold text-slate-500 shrink-0 ml-3">{tp.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-orange transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <Empty>No chatbot conversations yet.</Empty>}
        </div>
      </Panel>
      </div>

      {data.lead_quality.length > 0 && (
        <div className="mt-6">
        <Panel testid="analytics-quality" icon={Sparkles} iconCls="bg-fuchsia-50 text-fuchsia-600" title="Cassie lead quality" subtitle="How Cassie rated the intent of leads it captured.">
          <div className="flex flex-wrap gap-4">
            {data.lead_quality.map((q) => (
              <div key={q.name} data-testid={`quality-${q.name}`} className="flex items-center gap-3 bg-slate-50 rounded-xl px-5 py-3">
                <span className="w-3 h-3 rounded-full" style={{ background: QUALITY_COLORS[q.name] || "#94A3B8" }} />
                <span className="font-display font-extrabold text-2xl text-navy">{q.value}</span>
                <span className="text-sm font-semibold text-slate-500">{q.name} intent</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      )}

      {activeList && (
        <LeadListModal
          open={!!activeList}
          onClose={() => setActiveList(null)}
          title={activeList.title}
          columns={activeList.columns}
          fetcher={activeList.fetcher}
          filename={activeList.filename}
        />
      )}
    </div>
  );
}
